/**
 * Escritura en el libro (docs/PLAN-V4 §4). Independiente de Google: usa el puerto SheetsApi.
 *
 * Garantías:
 * - Concurrencia optimista: antes de cambiar una fila se relee y se compara su huella con la que tenía
 *   el dispositivo. Si alguien la cambió (otro dispositivo, a mano en la hoja), es un CONFLICTO y no se escribe.
 * - Inserciones idempotentes: cada fila nueva lleva su Id ANTES de escribirse; al reintentar se busca ese Id
 *   (o una fila idéntica sin Id) y nunca se duplica.
 * - Nunca se escriben las columnas calculadas (J:N y P de Movimientos).
 * Riesgo residual documentado: la API no tiene escritura condicional; entre la comprobación y la escritura
 * queda menos de un segundo en el que un cambio a mano podría sobrescribirse.
 */
import type { BudgetRow, Category, Movement, Part, Rule } from '@pf/domain';
import { isoToSerial, monthCell, num, text } from './cells.js';
import { hasMovement, movementFromRow, partFingerprint, ORDER_KIND_LABEL, type OrderRow, type AliasRow, type Flow, type MetaRow, type PlannedRow, type Snapshot, type Values } from './parse.js';
import { ADJUSTMENT_SOURCE, FORMULAS, ORDERS_HEADERS, ORDERS_TAB, PLANNED_HEADERS } from './schema.js';
import { addTricountExpense, removeTricountExpense, settleTricount, recordSettlement, linkSettlement, type Settlement, type TricountEntry, type TricountExpense } from './tricount.js';
import { deleteCategory, deleteRule, renameCategory, updateCategory } from './categories.js';

/** Puerto mínimo de la API de Google Sheets (valores sin formato, fechas como número de serie). */
export interface SheetsApi {
  get(range: string): Promise<Values>;
  /** RAW: valores tal cual; USER_ENTERED: fórmulas. */
  update(data: Array<{ range: string; values: Values }>, input?: 'RAW' | 'USER_ENTERED'): Promise<void>;
  /** Añade filas (insertándolas, nunca sobrescribiendo) tras la tabla del rango; devuelve el rango escrito ('Hoja!A842:I842'). */
  append(range: string, values: Values): Promise<{ updatedRange: string }>;
  clear(ranges: string[]): Promise<void>;
  /** Añade una pestaña (para crear las que faltan en libros anteriores); hidden: como _Meta, que no se vea al abrir el libro. */
  addSheet(title: string, opts?: { hidden?: boolean }): Promise<void>;
  /** Borra filas enteras (las de debajo suben). */
  deleteRows(rows: Array<{ tab: string; row: number }>): Promise<void>;
}

export type MovementChanges = Partial<Pick<Movement, 'date' | 'concept' | 'amount' | 'account' | 'category' | 'shared' | 'group' | 'note' | 'month'>>;

/** Datos de una cuenta que se escriben (lo calculado, como el saldo, lo pone la hoja). */
export interface AccountInput { name: string; bank: string; type: string; share: number; inNetWorth: boolean; opening: number; openingDate: string;
  /** Uso de la cuenta (Cuentas M): '' (sin decir), 'Disponible', 'Apartada: <para qué>' o 'Uso limitado'. */
  use?: string;
  /** Otras personas de la cuenta, separadas por comas (Cuentas O). */
  people?: string;
  /** Cómo se calcula tu parte (Cuentas P): '' o 'Fija' (el % de Propiedad) o 'Aportaciones' (según lo que aporta cada persona). */
  split?: string }
/** Columna M de Cuentas: el uso de la cuenta (0-based 12); su título se pone al escribir el primero. O y P: personas y reparto. */
export const USE_COL = 12, USE_HEADER = 'Uso';
const EXTRA_HEADERS = { M: USE_HEADER, O: 'Personas', P: 'Tu parte' } as const;
async function writeUse(api: SheetsApi, row: number, a: Pick<AccountInput, 'use' | 'people' | 'split'>) {
  const cells = ([['M', a.use], ['O', a.people], ['P', a.split]] as const).filter(([, v]) => v !== undefined);
  if (!cells.length) return;
  const head = await api.get('Cuentas!M1:P1');
  const have = (col: string) => text(head[0]?.[col.charCodeAt(0) - 77]);
  await api.update([
    ...cells.filter(([col]) => !have(col)).map(([col]) => ({ range: `Cuentas!${col}1`, values: [[EXTRA_HEADERS[col]]] })),
    ...cells.map(([col, v]) => ({ range: `Cuentas!${col}${row}`, values: [[v!]] })),
  ]);
}

export type Command =
  | { type: 'update-movement'; id: string; row: number; fingerprint: string; changes: MovementChanges }
  | { type: 'add-movement'; movement: Movement }
  /** Borra la fila del movimiento (y sus divisiones). Lo enviado a Tricount sigue allí. */
  | { type: 'delete-movement'; id: string; row: number; fingerprint: string }
  /** Sube los movimientos que quedaron debajo de filas vacías (p. ej. tras las filas preparadas con casillas). */
  | { type: 'compact-movements' }
  | { type: 'save-parts'; movementId: string; expected: string[]; parts: Part[] }
  | { type: 'upsert-budget'; budget: BudgetRow }
  | { type: 'delete-budget'; year: number; category: string }
  | { type: 'add-rule'; rule: Rule }
  | { type: 'add-category'; category: Category }
  /** Cambia el tipo, la naturaleza y las palabras clave de una categoría (no el nombre). */
  | { type: 'update-category'; name: string; category: Category }
  /** Renombra una categoría en todo el libro, o la fusiona con «to» si esa ya existe (ver categories.ts). */
  | { type: 'rename-category'; from: string; to: string }
  /** Elimina una categoría sin movimientos, con su presupuesto, sus notas y sus reglas. */
  | { type: 'delete-category'; name: string }
  /** Quita una regla de _Reglas. */
  | { type: 'delete-rule'; rule: Rule }
  | { type: 'assign-id'; row: number; fingerprint: string; id: string }
  | { type: 'clear-orphan-id'; row: number; id: string }
  | { type: 'rewrite-formulas'; cells: string[] }
  /** Foto de patrimonio de un mes. onlyIfMissing: las automáticas nunca pisan una que ya exista. */
  | { type: 'upsert-snapshot'; snapshot: Omit<Snapshot, 'row'>; onlyIfMissing?: boolean }
  /** Aportación (+) o reembolso (−) en Inversiones A:D; si el fondo no está en F, se añade. */
  | { type: 'add-flow'; flow: Flow }
  /** Precio de un fondo (Inversiones H:I); si el fondo no está en F, se añade. */
  | { type: 'set-price'; fund: string; price: number; date: string }
  /** Saldo que dice el banco para una cuenta en una fecha (Cuentas J:K). */
  | { type: 'set-bank-balance'; account: string; balance: number; date: string }
  /** Cuenta nueva (Cuentas A:G, en la primera fila libre). El nombre no se puede cambiar después: lo usan los movimientos. */
  | { type: 'add-account'; account: AccountInput }
  /** Cambia los datos de una cuenta (Cuentas B:G): banco, tipo, tu parte, si cuenta en el patrimonio y el saldo inicial. */
  | { type: 'update-account'; name: string; account: Omit<AccountInput, 'name'> }
  /** Apunta un gasto en el libro de Tricount y lo anota en _Meta (Tricount enviado / importe) para no repetirlo. */
  | { type: 'send-tricount'; movementId: string; expense: TricountExpense; sentAt: string }
  /** Quita un gasto de Tricount (copiándolo a «Quitados») y, si es de un movimiento tuyo, lo deja como no compartido y sin enviar. */
  | { type: 'unsend-tricount'; opId: string; movementId: string; entry: TricountEntry; removedAt: string; removedBy: string }
  /** Salda Tricount: Histórico + Saldos y vacía lo saldado (los movimientos de tu libro van en otros comandos del mismo envío). */
  | { type: 'settle-tricount'; settlement: Omit<Settlement, 'row' | 'count' | 'total' | 'since' | 'until'> }
  /** Apunta en «Saldos» un saldo antiguo (sin detalle) o pone el movimiento de un saldo pendiente de cobro. */
  | { type: 'record-settlement'; settlement: Omit<Settlement, 'row'> }
  | { type: 'link-settlement'; id: string; movementId: string }
  /** Importación: movimientos nuevos (cada uno con su Id) y su fila de _Meta, en un solo bloque reintentable. */
  | { type: 'import-batch'; label: string; movements: Movement[]; meta: MetaRow[] }
  /** "Es el mismo movimiento": recuerda la equivalencia para próximas importaciones (_Alias). */
  | { type: 'add-aliases'; aliases: AliasRow[] }
  /** Crea la pestaña Notas si no existe (libros anteriores a ella). */
  | { type: 'add-notes-tab' }
  /** Nota del mes para una categoría ('' la borra). */
  | { type: 'set-note'; month: string; category: string; note: string }
  /** Crea o cambia un gasto previsto (crea la pestaña Previstos si aún no existe). */
  | { type: 'upsert-planned'; planned: Omit<PlannedRow, 'row'> }
  | { type: 'delete-planned'; id: string }
  /** Pedidos de Amazon leídos de los emails (crea la pestaña oculta _Pedidos). Lo de un email que ya esté (misma Ref) no se repite. */
  | { type: 'add-orders'; rows: Array<Omit<OrderRow, 'row'>> }
  /** Enlaza lo de esos emails de _Pedidos (por su Ref) con el cobro del banco que los pagó ('' lo desenlaza). */
  | { type: 'link-orders'; refs: string[]; movementId: string };

/** Otros libros a los que puede escribir una orden (solo Tricount, de momento). */
export interface CommandContext { tricount?: SheetsApi }

export type CommandResult = { status: 'done' } | { status: 'conflict'; message: string };

const COLUMN: Record<keyof MovementChanges, string> = { date: 'A', concept: 'B', amount: 'C', account: 'D', category: 'E', shared: 'F', group: 'G', note: 'H', month: 'I' };

/** Celdas A:I de un movimiento, como se escriben en la hoja. */
export function movementCells(m: Pick<Movement, 'date' | 'concept' | 'amount' | 'account' | 'category' | 'shared' | 'group' | 'note' | 'month'>): unknown[] {
  return [m.date ? isoToSerial(m.date) : '', m.concept, m.amount, m.account, m.category, m.shared, m.group, m.note, m.month];
}

function cellValue(field: keyof MovementChanges, v: unknown): unknown {
  return field === 'date' && typeof v === 'string' && v ? isoToSerial(v) : v;
}

const ULID = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** Id estable, ordenado en el tiempo y aleatorio (estilo ULID), con prefijo legible. */
export function newId(prefix = 'M', now = Date.now(), rnd: () => number = Math.random): string {
  let t = now, time = '';
  for (let i = 0; i < 10; i++) { time = ULID[t % 32] + time; t = Math.floor(t / 32); }
  let r = '';
  for (let i = 0; i < 8; i++) r += ULID[Math.floor(rnd() * 32)];
  return `${prefix}-${time}${r}`;
}

/** Primera fila libre tras la última con algo en las columnas dadas (índices dentro del rango leído desde la fila 1). */
const nextFree = (all: Values, cols: number[]) => {
  let last = 1;
  all.forEach((r, i) => { if (cols.some(c => { const v = r?.[c]; return v !== '' && v !== null && v !== undefined; })) last = i + 1; });
  return last + 1;
};

/** Fila de un fondo en Inversiones!F (sin distinguir mayúsculas); si no está, lo escribe en la primera fila libre. */
async function fundRow(api: SheetsApi, fund: string): Promise<number> {
  const f = await api.get('Inversiones!F1:F');
  const i = f.findIndex((r, n) => n > 0 && text(r?.[0]).toLowerCase() === fund.toLowerCase());
  if (i > 0) return i + 1;
  const row = nextFree(f, [0]);
  await api.update([{ range: `Inversiones!F${row}`, values: [[fund]] }]);
  return row;
}

/** Columnas de Movimientos que indican que una fila tiene algo (A:I menos F: sus casillas vacías valen FALSO). */
const MOVEMENT_DATA_COLS = [0, 1, 2, 3, 4, 6, 7, 8];

const rowOf = (updatedRange: string) => Number(updatedRange.match(/![A-Z]+(\d+)/)?.[1] ?? NaN);

/**
 * Escribe movimientos nuevos justo debajo del último. No se usa «append» de Google: el libro trae filas preparadas
 * (casillas de Compartido) y Google las cuenta como tabla, así que añadía al final de todas, lejos de la vista.
 * Si dos escrituras eligen la misma fila a la vez, la que pierde lo detecta (su Id no está) y falla para reintentarse.
 */
export async function writeNewMovements(api: SheetsApi, items: Array<{ cells: Values[number]; id: string }>): Promise<number> {
  if (!items.length) return 0;
  const first = nextFree(await api.get('Movimientos!A1:I'), MOVEMENT_DATA_COLS);
  const last = first + items.length - 1;
  try {
    await api.update([{ range: `Movimientos!A${first}:I${last}`, values: items.map(i => i.cells) }, { range: `Movimientos!O${first}:O${last}`, values: items.map(i => [i.id]) }]);
  } catch (err) {
    // Sin filas libres en la hoja: entonces sí se añaden (insertándolas) al final
    if (!/exceeds grid limits|max rows/i.test((err as Error).message)) throw err;
    const { updatedRange } = await api.append('Movimientos!A1:I1', items.map(i => i.cells));
    const row = rowOf(updatedRange);
    if (!Number.isFinite(row)) throw new Error(`Respuesta inesperada al añadir: ${updatedRange}`);
    await api.update([{ range: `Movimientos!O${row}:O${row + items.length - 1}`, values: items.map(i => [i.id]) }]);
    return row;
  }
  const ids = await api.get(`Movimientos!O${first}:O${last}`);
  if (items.some((it, k) => text(ids[k]?.[0]) !== it.id)) throw new Error('Otra escritura ha usado las mismas filas a la vez; se reintentará');
  return first;
}

async function findIdRow(api: SheetsApi, id: string): Promise<number | null> {
  const col = await api.get('Movimientos!O1:O');
  const i = col.findIndex(r => text(r?.[0]) === id);
  return i < 0 ? null : i + 1;
}

/** Fila actual de un movimiento (la esperada o, si se movió, donde esté su Id). */
async function locate(api: SheetsApi, id: string, row: number) {
  const read = async (r: number) => movementFromRow((await api.get(`Movimientos!A${r}:O${r}`))[0] ?? [], r);
  let m = await read(row);
  if (m?.id === id) return m;
  const at = await findIdRow(api, id);
  if (at === null) return null;
  m = await read(at);
  return m?.id === id ? m : null;
}

export async function executeCommand(api: SheetsApi, cmd: Command, attempt = 1, ctx: CommandContext = {}): Promise<CommandResult> {
  switch (cmd.type) {
    case 'update-movement': {
      const m = await locate(api, cmd.id, cmd.row);
      if (!m) return { status: 'conflict', message: 'El movimiento ya no está en el libro (¿se borró?)' };
      // ¿Ya aplicado (reintento tras una respuesta perdida)?
      const fields = Object.keys(cmd.changes) as Array<keyof MovementChanges>;
      if (fields.every(f => m[f] === cmd.changes[f])) return { status: 'done' };
      if (m.fingerprint !== cmd.fingerprint) return { status: 'conflict', message: 'El movimiento ha cambiado en otro sitio desde que lo cargaste' };
      await api.update(fields.map(f => ({ range: `Movimientos!${COLUMN[f]}${m.row}`, values: [[cellValue(f, cmd.changes[f])]] })));
      return { status: 'done' };
    }

    case 'add-movement': {
      const id = cmd.movement.id;
      if (!id) throw new Error('Un movimiento nuevo necesita su Id antes de escribirse');
      if (await findIdRow(api, id) !== null) return { status: 'done' };
      const cells = movementCells(cmd.movement);
      if (attempt > 1) {
        // Un intento anterior pudo añadir la fila y fallar antes de escribir el Id: se completa en vez de duplicar
        const all = await api.get('Movimientos!A1:O');
        const i = all.findIndex((r, n) => n > 0 && !text(r?.[14]) && JSON.stringify((r ?? []).slice(0, 9).map(v => v ?? '')) === JSON.stringify(cells));
        if (i > 0) { await api.update([{ range: `Movimientos!O${i + 1}`, values: [[id]] }]); return { status: 'done' }; }
      }
      await writeNewMovements(api, [{ cells, id }]);
      return { status: 'done' };
    }

    case 'delete-movement': {
      const m = await locate(api, cmd.id, cmd.row);
      if (!m) return { status: 'done' };   // ya no está (borrado antes o en otro sitio)
      if (m.fingerprint !== cmd.fingerprint) return { status: 'conflict', message: 'El movimiento ha cambiado en otro sitio desde que lo cargaste' };
      const parts = (await api.get('Divisiones!A1:A')).map((r, i) => ({ id: text(r?.[0]), row: i + 1 })).filter(x => x.row > 1 && x.id === cmd.id);
      await api.deleteRows([{ tab: 'Movimientos', row: m.row }, ...parts.map(p => ({ tab: 'Divisiones', row: p.row }))]);
      return { status: 'done' };
    }

    case 'compact-movements': {
      const all = await api.get('Movimientos!A1:O');
      const filled = (r: unknown[] | undefined) => MOVEMENT_DATA_COLS.some(c => { const v = r?.[c]; return v !== '' && v !== null && v !== undefined; });
      const rows = all.map((r, i) => ({ r, row: i + 1 })).filter(x => x.row > 1 && filled(x.r));
      const moves = rows.map((x, k) => ({ ...x, to: k + 2 })).filter(x => x.to !== x.row);
      if (!moves.length) return { status: 'done' };
      // Todo en memoria y en una sola escritura (A:I y el Id; J:N y P son fórmulas de columna); luego se vacían las de abajo
      const cells = (r: unknown[]) => Array.from({ length: 9 }, (_, c) => r[c] ?? '');
      await api.update(moves.flatMap(x => [{ range: `Movimientos!A${x.to}:I${x.to}`, values: [cells(x.r)] }, { range: `Movimientos!O${x.to}`, values: [[x.r[14] ?? '']] }]));
      const targets = new Set(moves.map(x => x.to));
      const freed = moves.filter(x => !targets.has(x.row));
      if (freed.length) await api.clear(freed.flatMap(x => [`Movimientos!A${x.row}:I${x.row}`, `Movimientos!O${x.row}`]));
      return { status: 'done' };
    }

    case 'save-parts': {
      const all = await api.get('Divisiones!A1:D');
      const mine = all.map((r, i) => ({ r, row: i + 1 })).filter(x => x.row > 1 && text(x.r?.[0]) === cmd.movementId);
      const current = mine.map(x => partFingerprint({ movementId: text(x.r[0]), category: text(x.r[1]), amount: Number(x.r[2]) || 0, note: text(x.r[3]) }));
      const wanted = cmd.parts.map(partFingerprint);
      if (JSON.stringify(current) === JSON.stringify(wanted)) return { status: 'done' };
      if (JSON.stringify(current) !== JSON.stringify(cmd.expected)) return { status: 'conflict', message: 'Las divisiones de este movimiento han cambiado en otro sitio' };
      const rows = cmd.parts.map(p => [p.movementId, p.category, p.amount, p.note]);
      const reuse = mine.slice(0, rows.length), extra = mine.slice(rows.length);
      if (reuse.length) await api.update(reuse.map((x, i) => ({ range: `Divisiones!A${x.row}:D${x.row}`, values: [rows[i]!] })));
      if (extra.length) await api.clear(extra.map(x => `Divisiones!A${x.row}:D${x.row}`));
      for (const r of rows.slice(reuse.length)) await api.append('Divisiones!A1:D1', [r]);
      return { status: 'done' };
    }

    case 'upsert-budget': {
      const b = cmd.budget;
      const all = await api.get('Presupuesto!A1:Q');
      const i = all.findIndex((r, n) => n > 0 && Number(r?.[0]) === b.year && text(r?.[1]).toLowerCase() === b.category.toLowerCase());
      const values = [[b.year, b.category, b.monthly, ...b.overrides.map(o => (o === null ? '' : o)), b.warn, b.note]];
      if (i > 0) await api.update([{ range: `Presupuesto!A${i + 1}:Q${i + 1}`, values }]);
      else await api.append('Presupuesto!A1:Q1', values);
      return { status: 'done' };
    }

    case 'delete-budget': {
      const all = await api.get('Presupuesto!A1:Q');
      const i = all.findIndex((r, n) => n > 0 && Number(r?.[0]) === cmd.year && text(r?.[1]).toLowerCase() === cmd.category.toLowerCase());
      if (i > 0) await api.clear([`Presupuesto!A${i + 1}:Q${i + 1}`]);
      return { status: 'done' };
    }

    case 'add-category': {
      const c = cmd.category;
      const all = await api.get("'Categorías'!A1:A");
      if (all.some((r, n) => n > 0 && text(r?.[0]).toLowerCase() === c.name.toLowerCase())) return { status: 'done' };
      const kind = c.kind === 'income' ? 'Ingreso' : c.kind === 'excluded' ? 'No cuenta' : 'Gasto';
      const nature = c.nature === 'fixed' ? 'Fijo' : c.nature === 'variable' ? 'Variable' : '';
      await api.append("'Categorías'!A1:D1", [[c.name, kind, nature, c.keywords.join(', ')]]);
      return { status: 'done' };
    }

    case 'update-category': return updateCategory(api, cmd.name, cmd.category);
    case 'rename-category': return renameCategory(api, cmd.from, cmd.to);
    case 'delete-category': return deleteCategory(api, cmd.name);
    case 'delete-rule': return deleteRule(api, cmd.rule);

    case 'add-rule': {
      const r = cmd.rule;
      const values = [r.priority ?? '', r.account, r.contains, r.regex, r.amountEquals ?? '', r.direction, r.category, r.active];
      const all = await api.get('_Reglas!A1:H');
      if (all.some((x, n) => n > 0 && JSON.stringify((x ?? []).slice(0, 7).map(v => text(v))) === JSON.stringify(values.slice(0, 7).map(v => text(v))))) return { status: 'done' };
      await api.append('_Reglas!A1:H1', [values]);
      return { status: 'done' };
    }

    case 'assign-id': {
      const r = (await api.get(`Movimientos!A${cmd.row}:O${cmd.row}`))[0] ?? [];
      if (!hasMovement(r)) return { status: 'done' };   // fila sin movimiento: nunca se le pone Id
      const m = movementFromRow(r, cmd.row);
      if (m?.id === cmd.id) return { status: 'done' };
      if (!m || m.id || m.fingerprint !== cmd.fingerprint) return { status: 'conflict', message: 'La fila ha cambiado; vuelve a sincronizar antes de asignar el Id' };
      await api.update([{ range: `Movimientos!O${cmd.row}`, values: [[cmd.id]] }]);
      return { status: 'done' };
    }

    case 'clear-orphan-id': {
      const r = (await api.get(`Movimientos!A${cmd.row}:O${cmd.row}`))[0] ?? [];
      if (hasMovement(r) || text(r[14]) !== cmd.id) return { status: 'done' };   // solo si sigue siendo un Id suelto
      await api.clear([`Movimientos!O${cmd.row}`]);
      return { status: 'done' };
    }

    case 'upsert-snapshot': {
      const sn = cmd.snapshot;
      const all = await api.get('Patrimonio!A1:E');
      const i = all.findIndex((r, n) => n > 0 && monthCell(r?.[0]) === sn.month);
      if (i > 0 && cmd.onlyIfMissing) return { status: 'done' };
      // D es una fórmula (Total): se escriben A:C y E por separado
      const row = i > 0 ? i + 1 : nextFree(all, [0, 1, 2, 4]);
      await api.update([{ range: `Patrimonio!A${row}:C${row}`, values: [[sn.month, sn.cash, sn.investments]] }, { range: `Patrimonio!E${row}`, values: [[sn.source]] }]);
      return { status: 'done' };
    }

    case 'add-flow': {
      const f = cmd.flow;
      const cells = [isoToSerial(f.date), f.fund, f.amount, f.units ?? ''];
      await fundRow(api, f.fund);
      const all = await api.get('Inversiones!A1:D');
      // Sin Id: al reintentar, una fila idéntica significa que el intento anterior ya la escribió
      if (attempt > 1 && all.some((r, n) => n > 0 && num(r?.[0]) === cells[0] && text(r?.[1]) === f.fund && num(r?.[2]) === f.amount && (num(r?.[3]) ?? '') === cells[3])) return { status: 'done' };
      const row = nextFree(all, [0, 1, 2, 3]);
      await api.update([{ range: `Inversiones!A${row}:D${row}`, values: [cells] }]);
      return { status: 'done' };
    }

    case 'set-price': {
      const row = await fundRow(api, cmd.fund);
      await api.update([{ range: `Inversiones!H${row}:I${row}`, values: [[cmd.price, isoToSerial(cmd.date)]] }]);
      return { status: 'done' };
    }

    case 'add-account': {
      const a = cmd.account;
      const names = await api.get('Cuentas!A1:A');
      if (names.some((r, n) => n > 0 && text(r?.[0]).toLowerCase() === a.name.toLowerCase())) return { status: 'done' };
      // Primera fila sin nombre (no «append»: las columnas calculadas llegan hasta abajo y la API añadiría al final)
      let row = names.findIndex((r, n) => n > 0 && !text(r?.[0])) + 1;
      if (row <= 0) row = Math.max(names.length, 1) + 1;
      await api.update([{ range: `Cuentas!A${row}:G${row}`, values: [[a.name, a.bank, a.type, a.share, a.inNetWorth, a.opening, isoToSerial(a.openingDate)]] }]);
      await writeUse(api, row, a);
      return { status: 'done' };
    }

    case 'update-account': {
      const names = await api.get('Cuentas!A1:A');
      const i = names.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.name);
      if (i < 0) return { status: 'conflict', message: `La cuenta ${cmd.name} ya no está en el libro` };
      const a = cmd.account;
      await api.update([{ range: `Cuentas!B${i + 1}:G${i + 1}`, values: [[a.bank, a.type, a.share, a.inNetWorth, a.opening, isoToSerial(a.openingDate)]] }]);
      await writeUse(api, i + 1, a);
      return { status: 'done' };
    }

    case 'set-bank-balance': {
      const names = await api.get('Cuentas!A1:A');
      const i = names.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.account);
      if (i < 0) return { status: 'conflict', message: `La cuenta ${cmd.account} ya no está en el libro` };
      await api.update([{ range: `Cuentas!J${i + 1}:K${i + 1}`, values: [[cmd.balance, isoToSerial(cmd.date)]] }]);
      return { status: 'done' };
    }

    case 'send-tricount': {
      if (!ctx.tricount) return { status: 'conflict', message: 'No tienes conectado tu libro de Tricount' };
      const all = await api.get('_Meta!A1:G');
      const i = all.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.movementId);
      if (i > 0 && text(all[i]?.[5])) return { status: 'done' };   // ya consta como enviado
      await addTricountExpense(ctx.tricount, cmd.expense);           // idempotente: no duplica al reintentar
      const sent = [isoToSerial(cmd.sentAt), cmd.expense.amount];
      if (i > 0) await api.update([{ range: `_Meta!F${i + 1}:G${i + 1}`, values: [sent] }]);
      else { const row = nextFree(all, [0]); await api.update([{ range: `_Meta!A${row}:G${row}`, values: [[cmd.movementId, 'app', '', '', '', ...sent]] }]); }
      return { status: 'done' };
    }

    case 'settle-tricount':
      if (!ctx.tricount) return { status: 'conflict', message: 'No tienes conectado tu libro de Tricount' };
      await settleTricount(ctx.tricount, cmd.settlement);
      return { status: 'done' };
    case 'record-settlement':
      if (!ctx.tricount) return { status: 'conflict', message: 'No tienes conectado tu libro de Tricount' };
      await recordSettlement(ctx.tricount, cmd.settlement);
      return { status: 'done' };
    case 'link-settlement':
      if (!ctx.tricount) return { status: 'conflict', message: 'No tienes conectado tu libro de Tricount' };
      await linkSettlement(ctx.tricount, cmd.id, cmd.movementId);
      return { status: 'done' };

    case 'unsend-tricount': {
      if (!ctx.tricount) return { status: 'conflict', message: 'No tienes conectado tu libro de Tricount' };
      await removeTricountExpense(ctx.tricount, cmd.entry, { id: cmd.opId, at: cmd.removedAt, by: cmd.removedBy });
      if (cmd.movementId) {
        const [ids, meta] = await Promise.all([api.get('Movimientos!O1:O'), api.get('_Meta!A1:G')]);
        const i = ids.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.movementId);
        const j = meta.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.movementId);
        const data: Array<{ range: string; values: Values }> = [];
        if (i > 0) data.push({ range: `Movimientos!F${i + 1}`, values: [[false]] });
        if (j > 0) data.push({ range: `_Meta!F${j + 1}:G${j + 1}`, values: [['', '']] });
        if (data.length) await api.update(data);
      }
      return { status: 'done' };
    }

    case 'import-batch': {
      // 1) Movimientos: solo los que no estén ya (por Id); al reintentar, completa filas añadidas sin Id
      const present = new Set((await api.get('Movimientos!O1:O')).map(r => text(r?.[0])).filter(Boolean));
      let todo = cmd.movements.filter(m => !present.has(m.id));
      if (attempt > 1 && todo.length) {
        const all = await api.get('Movimientos!A1:O');
        const fixes: Array<{ range: string; values: Values }> = [];
        todo = todo.filter(m => {
          const cells = JSON.stringify(movementCells(m));
          const i = all.findIndex((r, n) => n > 0 && !text(r?.[14]) && JSON.stringify((r ?? []).slice(0, 9).map(v => v ?? '')) === cells);
          if (i < 0) return true;
          all[i]![14] = m.id;   // que no la reutilice otro movimiento idéntico del mismo lote
          fixes.push({ range: `Movimientos!O${i + 1}`, values: [[m.id]] });
          return false;
        });
        if (fixes.length) await api.update(fixes);
      }
      // Protección contra importar dos veces lo mismo (otro dispositivo, una revisión repetida): se salta lo que ya esté
      // por su referencia del banco o por cuenta + fecha + importe + saldo tras (o, sin saldo, + concepto y fichero)
      const skipped = new Set<string>();
      if (todo.length) {
        const metaRows = await api.get('_Meta!A1:H');
        const metaOf = new Map(metaRows.slice(1).map(r => [text(r?.[0]), { source: text(r?.[1]), ref: text(r?.[2]), bal: r?.[4] === '' || r?.[4] === undefined || r?.[4] === null ? null : num(r?.[4]) }]));
        const keyOf = (account: string, date: unknown, amount: number, concept: string, m: { source: string; ref: string; bal: number | null } | undefined) => {
          if (!m || m.source === ADJUSTMENT_SOURCE) return '';
          if (m.source.startsWith('sync:') && m.ref) return `ref|${m.source}|${m.ref}`;
          if (m.bal !== null) return `bal|${account}|${date}|${amount}|${m.bal}`;
          return m.source.startsWith('file:') ? `file|${account}|${date}|${amount}|${concept}|${m.source}|${m.ref}` : '';
        };
        const seen = new Map<string, number>();
        for (const r of (await api.get('Movimientos!A1:O')).slice(1)) {
          const k = keyOf(text(r?.[3]), r?.[0], Number(r?.[2]), text(r?.[1]), metaOf.get(text(r?.[14])));
          if (k) seen.set(k, (seen.get(k) ?? 0) + 1);
        }
        const mine = new Map(cmd.meta.map(m => [m.id, { source: m.source, ref: m.sourceRef, bal: m.balanceAfter }]));
        todo = todo.filter(m => {
          const k = keyOf(m.account, isoToSerial(m.date), m.amount, m.concept, mine.get(m.id));
          if (!k || !seen.get(k)) return true;
          seen.set(k, seen.get(k)! - 1);   // dos movimientos idénticos legítimos: solo se salta los que ya estén
          skipped.add(m.id);
          return false;
        });
      }
      await writeNewMovements(api, todo.map(m => ({ cells: movementCells(m), id: m.id })));
      // 2) _Meta de los que aún no la tengan (y no de los saltados)
      const metaIds = new Set((await api.get('_Meta!A1:A')).map(r => text(r?.[0])));
      const meta = cmd.meta.filter(m => !metaIds.has(m.id) && !skipped.has(m.id));
      if (meta.length) await api.append('_Meta!A1:H1', meta.map(m => [m.id, m.source, m.sourceRef, m.valueDate ? isoToSerial(m.valueDate) : '',
        m.balanceAfter ?? '', m.tricountSentAt ? isoToSerial(m.tricountSentAt) : '', m.tricountAmount ?? '', m.importedAt ? isoToSerial(m.importedAt) : '']));
      return { status: 'done' };
    }

    case 'add-aliases': {
      const all = await api.get('_Alias!A1:E');
      const has = (a: AliasRow) => all.some((r, n) => n > 0 && text(r?.[4]) === a.id && text(r?.[0]) === a.account && text(r?.[3]) === a.description && num(r?.[2]) === a.amount);
      const rows = cmd.aliases.filter(a => !has(a));
      if (rows.length) await api.append('_Alias!A1:E1', rows.map(a => [a.account, isoToSerial(a.date), a.amount, a.description, a.id]));
      return { status: 'done' };
    }

    case 'add-notes-tab': {
      let head: Values | null = null;
      try { head = await api.get('Notas!A1:C1'); } catch { /* no existe: se crea */ }
      if (head && text(head[0]?.[0])) return { status: 'done' };
      if (!head) await api.addSheet('Notas');
      await api.update([{ range: 'Notas!A1:C1', values: [['Mes', 'Categoría', 'Nota']] }]);
      return { status: 'done' };
    }

    case 'set-note': {
      const all = await api.get('Notas!A1:C');
      const i = all.findIndex((r, n) => n > 0 && monthCell(r?.[0]) === cmd.month && text(r?.[1]).toLowerCase() === cmd.category.toLowerCase());
      const note = cmd.note.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, 300);
      if (i > 0 && !note) await api.clear([`Notas!A${i + 1}:C${i + 1}`]);
      else if (i > 0) await api.update([{ range: `Notas!C${i + 1}`, values: [[note]] }]);
      else if (note) { const row = nextFree(all, [0, 1, 2]); await api.update([{ range: `Notas!A${row}:C${row}`, values: [[cmd.month, cmd.category, note]] }]); }
      return { status: 'done' };
    }

    case 'upsert-planned': {
      const p = cmd.planned;
      let all: Values | null = null;
      try { all = await api.get('Previstos!A1:L'); } catch { /* no existe: se crea */ }
      if (!all) { await api.addSheet('Previstos'); all = []; }
      if (!text(all[0]?.[0])) await api.update([{ range: 'Previstos!A1:L1', values: [PLANNED_HEADERS] }]);
      const clean = (v: string, max: number) => v.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().replace(/^[=+\-@]+/, '').slice(0, max);
      const cells = [p.id, p.category, p.month, Math.round(p.amount * 100) / 100, clean(p.concept, 120), clean(p.keyword, 40), p.repeat === 'once' ? 'Solo un año' : 'Cada año',
        p.from, p.to ?? '', p.budget === 'included' ? 'Incluido' : 'Suma', p.linked.join(', '), p.unlinked.join(', ')];
      const i = all.findIndex((r, n) => n > 0 && text(r?.[0]) === p.id);
      const row = i > 0 ? i + 1 : nextFree(all.length ? all : [[]], [0, 1]);
      await api.update([{ range: `Previstos!A${row}:L${row}`, values: [cells] }]);
      return { status: 'done' };
    }

    case 'delete-planned': {
      let all: Values = [];
      try { all = await api.get('Previstos!A1:A'); } catch { return { status: 'done' }; }
      const i = all.findIndex((r, n) => n > 0 && text(r?.[0]) === cmd.id);
      if (i > 0) await api.deleteRows([{ tab: 'Previstos', row: i + 1 }]);
      return { status: 'done' };
    }

    case 'add-orders': {
      let all: Values | null = null;
      try { all = await api.get(`${ORDERS_TAB}!A1:I`); } catch { /* no existe: se crea */ }
      if (!all) { await api.addSheet(ORDERS_TAB, { hidden: true }); all = []; }
      if (!text(all[0]?.[0])) await api.update([{ range: `${ORDERS_TAB}!A1:I1`, values: [ORDERS_HEADERS] }]);
      const have = new Set(all.slice(1).map(r => text(r?.[7])));
      const clean = (v: string, max: number) => v.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().replace(/^[=+\-@]+/, '').slice(0, max);
      const rows = cmd.rows.filter(r => r.ref && !have.has(r.ref));
      if (rows.length) await api.append(`${ORDERS_TAB}!A1:I1`, rows.map(r => [isoToSerial(r.date), ORDER_KIND_LABEL[r.kind], clean(r.orderId, 40), clean(r.item, 100),
        r.qty, r.amount ?? '', r.total ?? '', r.ref, r.movementId]));
      return { status: 'done' };
    }

    case 'link-orders': {
      const all = await api.get(`${ORDERS_TAB}!H1:I`);
      const refs = new Set(cmd.refs);
      const mine = all.map((r, i) => ({ ref: text(r?.[0]), linked: text(r?.[1]), row: i + 1 })).filter(x => x.row > 1 && refs.has(x.ref));
      if (!mine.length) return { status: 'conflict', message: 'Ese pedido ya no está en _Pedidos' };
      if (cmd.movementId && mine.some(x => x.linked && x.linked !== cmd.movementId)) return { status: 'conflict', message: 'Ese pedido ya está enlazado con otro cobro' };
      const change = mine.filter(x => x.linked !== cmd.movementId);
      if (change.length) await api.update(change.map(x => ({ range: `${ORDERS_TAB}!I${x.row}`, values: [[cmd.movementId]] })));
      return { status: 'done' };
    }

    case 'rewrite-formulas': {
      const data = cmd.cells.filter(c => FORMULAS[c]).map(c => ({ range: c, values: [[FORMULAS[c]!]] }));
      if (data.length) await api.update(data, 'USER_ENTERED');
      return { status: 'done' };
    }
  }
}
